import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Image,
  Text,
  Center,
  Box,
  Spinner,
  Skeleton,
} from '@chakra-ui/react';
import DummyImage from '@/assets/dummy.png';
import { useEffect, useState } from 'react';

interface MaintenanceImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: Array<{
    image?: string;
    image_medium?: string;
    image_small?: string;
    comment?: string;
  }>;
  isLoading?: boolean;
  title?: string;
}

const MaintenanceImageModal: React.FC<MaintenanceImageModalProps> = ({
  isOpen,
  onClose,
  images,
  isLoading = false,
  title = 'ORDER COMPLETION IMAGES',
}) => {
  const [imageLoadingStates, setImageLoadingStates] = useState<boolean[]>([]);

  useEffect(() => {
    if (isOpen && images.length > 0) {
      setImageLoadingStates(new Array(images.length).fill(true));
    }
  }, [isOpen, images.length]);

  const handleImageLoad = (index: number) => {
    setImageLoadingStates((prev) => {
      const newStates = [...prev];
      newStates[index] = false;
      return newStates;
    });
  };

  const handleImageError = (index: number) => {
    setImageLoadingStates((prev) => {
      const newStates = [...prev];
      newStates[index] = false;
      return newStates;
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'md', md: 'lg' }} isCentered>
      <ModalOverlay bg="rgba(0, 0, 0, 0.6)" backdropFilter="blur(4px)" />
      <ModalContent bg="background.secondary" rounded="xl" maxW="600px">
        <ModalHeader
          fontSize="lg"
          fontWeight="semibold"
          fontFamily="primary"
          color="text.primary"
          bg="white"
          borderRadius="12px 12px 0 0"
          textAlign="center"
          py={4}
        >
          {title.toUpperCase()}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6} px={{ base: 4, md: 6 }}>
          {isLoading ? (
            <Center h="300px">
              <Spinner size="lg" color="brand.primary.DEFAULT" thickness="3px" />
            </Center>
          ) : images.length === 0 ? (
            <Center h="200px">
              <Text color="text.secondary" fontSize="md">
                No images uploaded
              </Text>
            </Center>
          ) : (
            <VStack spacing={8} align="stretch">
              {images.map((img, idx) => {
                const imageUrl = img.image_medium || img.image || img.image_small || DummyImage;
                const isImageLoading = imageLoadingStates[idx] ?? true;

                return (
                  <Box key={idx} textAlign="center">
                    <Box
                      position="relative"
                      height={{ base: '280px', md: '360px' }}
                      width="100%"
                      borderRadius="md"
                      overflow="hidden"
                      bg="gray.100"
                      boxShadow="sm"
                    >
                      {isImageLoading && (
                        <Center position="absolute" inset={0} bg="whiteAlpha.800" zIndex={2}>
                          <Spinner size="lg" color="brand.primary.DEFAULT" thickness="3px" />
                        </Center>
                      )}

                      <Skeleton
                        isLoaded={!isImageLoading}
                        height="100%"
                        width="100%"
                        borderRadius="md"
                      >
                        <Image
                          src={imageUrl}
                          alt={`ORDER COMPLETION IMAGE ${idx + 1}`}
                          height="100%"
                          width="100%"
                          objectFit="contain"
                          fallbackSrc={DummyImage}
                          onLoad={() => handleImageLoad(idx)}
                          onError={() => handleImageError(idx)}
                          loading="eager"
                        />
                      </Skeleton>
                    </Box>

                    {img.comment && (
                      <Box mt={4}>
                        <Text fontSize="sm" color="text.secondary" mb={1} fontWeight="medium">
                          Comment:
                        </Text>
                        <Text
                          fontSize="sm"
                          color="text.primary"
                          fontStyle="italic"
                          textAlign="center"
                          px={2}
                        >
                          "{img.comment}"
                        </Text>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default MaintenanceImageModal;
